import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { FEATURED_PLAYLIST_ID, PUBLIC_FEATURED_PLAYLIST_ID } from 'rev-portal/media/playlists/Playlists.Constant';
import { SearchService } from 'rev-portal/search/Search.Service';
import { ThemeService } from 'rev-portal/branding/Theme.Service';
import { getFiltersQuery } from 'rev-portal/media/search/SearchFilterState.Service';

import { DateParsersService } from 'rev-shared/date/DateParsers.Service';
import { PushService } from 'rev-shared/push/PushService';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { lastValueFrom } from 'rev-shared/rxjs/lastValueFrom';

import { IDynamicPlaylistSeachFilter, IPlaylist, IPlaylistData, PlaylistType } from './IPlaylist';
import { IMediaFeatures } from './IMediaFeatures';
import { MediaFeaturesService } from './MediaFeatures.Service';
import { VideoService, getThumbnailCfg } from './Video.Service';

export interface ISavePlaylist {
	playlistId: string;
	name: string;
	ownerUserIds?: string[];
	videoIds?: string[];
	searchFilter?: IDynamicPlaylistSeachFilter;
}

@Injectable({
	providedIn: 'root'
})
export class PlaylistService {

	constructor(
		private readonly DateParsers: DateParsersService,
		private readonly PushService: PushService,
		private readonly SearchService: SearchService,
		private readonly UserContext: UserContextService,
		private readonly VideoService: VideoService,
		private readonly ThemeService: ThemeService,
		private readonly http: HttpClient,
		private readonly MediaFeaturesService: MediaFeaturesService,
	) { }

	public get enablePublicFeaturedPlaylist(): boolean {
		return this.MediaFeaturesService.accountFeatures.enableGuestAccess && !!this.ThemeService.brandingSettings.publicHomeSettings?.defaultFeaturedVideosPlaylist;
	}

	public addFeaturedVideo(video: any): Promise<void> {
		return this.PushService.dispatchCommand('media:AddFeaturedVideo', video);
	}

	public addPublicFeaturedVideo(video: any): Promise<void> {
		return this.PushService.dispatchCommand('media:AddPublicFeaturedVideo', video);
	}

	public addVideoToPlaylists(videoId: string, userId: string, playlistIds: string[]): Promise<void> {
		return this.PushService.dispatchCommand('media:AddVideoToPlaylists', {
			videoId,
			userId,
			playlistIds
		});
	}

	public createPlaylist(playlist: { name: string, userId: string, playlistType: PlaylistType, searchFilter?: IDynamicPlaylistSeachFilter }): Promise<string> {
		return this.PushService.dispatchCommand('media:CreatePlaylist', playlist, 'PlaylistCreated')
			.then(result => result.message.playlistId);
	}

	public deletePlaylist(playlistId: string): Promise<void> {
		return this.PushService.dispatchCommand('media:DeletePlaylist', { playlistId });
	}

	public getFeaturedContent(accountId: string, fetchActiveVideosOnly?: boolean, mediaFeatures?: IMediaFeatures): Promise<any> {
		return lastValueFrom(this.http.get<any>(`/media/videos/accounts/${accountId}/featured-content`,
			{
				params: this.getIsActiveParam(fetchActiveVideosOnly)
			})).then(result => this.processFeaturedVideoContent(result, FEATURED_PLAYLIST_ID, mediaFeatures));
	}

	public getPublicFeaturedContent(accountId: string, fetchActiveVideosOnly?: boolean, mediaFeatures?: IMediaFeatures): Promise<any> {
		return lastValueFrom(this.http.get<any>(`/media/videos/accounts/${accountId}/public-featured-content`,
			{
				params: this.getIsActiveParam(fetchActiveVideosOnly)
			})).then(result => this.processFeaturedVideoContent(result, PUBLIC_FEATURED_PLAYLIST_ID, mediaFeatures));
	}

	public getFeaturedVideos(accountId: string, fetchActiveVideosOnly?: boolean, mediaFeatures?: IMediaFeatures): Promise<any> {
		return lastValueFrom(this.http.get<any>(`/media/videos/accounts/${accountId}/featured-videos`,
			{
				params: this.getIsActiveParam(fetchActiveVideosOnly)
			})).then(result => this.processFeaturedVideoContent(result, FEATURED_PLAYLIST_ID, mediaFeatures));
	}

	public getPublicFeaturedVideos(accountId: string, fetchActiveVideosOnly?: boolean, mediaFeatures?: IMediaFeatures): Promise<any> {
		return lastValueFrom(this.http.get<any>(`/media/videos/accounts/${accountId}/public-featured-videos`,
			{
				params: this.getIsActiveParam(fetchActiveVideosOnly)
			}))
			.then(result => this.processFeaturedVideoContent(result, PUBLIC_FEATURED_PLAYLIST_ID, mediaFeatures))
			.catch(() => {
				// In case the endpoint is not found or user doesn't have access, return an empty playlist instead of failing
				return this.processFeaturedVideoContent({
					videos: [],
					whenVideoAddedOrDeleted: { when: new Date() },
					createdBy: { when: new Date() }
				}, PUBLIC_FEATURED_PLAYLIST_ID,
				mediaFeatures);
			});
	}

	private processFeaturedVideoContent(result: any, id: string, mediaFeatures?: IMediaFeatures): Promise<any> {
		result.whenVideoAddedOrDeleted = result.whenVideoAddedOrDeleted || {};
		result.whenVideoAddedOrDeleted.when = this.DateParsers.parseUTCDate(result.whenVideoAddedOrDeleted.when);
		result.createdBy.when = this.DateParsers.parseUTCDate(result.createdBy.when);

		result.videos.forEach((video, playlistIndex) => Object.assign(video, {
			duration: this.DateParsers.parseTimespan(video.duration),
			thumbnailUri: video.thumbnailUri || '',
			thumbnailCfg: getThumbnailCfg(video.thumbnailCfg),
			uploaderFirstName: video.uploaderFirstName || '',
			subtitles: (video.transcriptionFiles || []).map(this.VideoService.toPlayerDirectiveSubtitle),
			playlistIndex,
			ownerUserId: video.owner?.userId || '',
			ownerUsername: video.owner?.username || '',
			ownerFirstName: video.owner?.firstName || '',
			ownerLastName: video.owner?.lastName || '',
			ownerProfileImageUri: video.owner?.profileImageUri,
			ownerFullName: this.shapeOwnerFullName(video.owner?.firstName, video.owner?.lastName),
			ratingsCount: video.ratingCount,
			videoOverlays: mediaFeatures ? this.VideoService.getViewerIdOverlays(mediaFeatures, video.viewerIdEnabled) : undefined
		}));

		return Object.assign(result, {
			count: result.videos.length,
			id,
			totalVideos: result.videos.length
		});
	}

	public getPlaylist(playlistId: string, fetchActiveVideosOnly?: boolean): Promise<IPlaylistData> {
		const accountId = this.UserContext.getAccount().id;

		if (playlistId === FEATURED_PLAYLIST_ID) {
			return this.getFeaturedVideos(accountId, fetchActiveVideosOnly);
		}
		if (playlistId === PUBLIC_FEATURED_PLAYLIST_ID && this.enablePublicFeaturedPlaylist) {
			return this.getPublicFeaturedVideos(accountId, fetchActiveVideosOnly);
		}

		return lastValueFrom(this.http.get<any>(`/media/playlists/${playlistId}`,
			{
				params: this.getIsActiveParam(fetchActiveVideosOnly)
			}))
			.then(result => {
				result.videos?.forEach((v, i) => Object.assign(v, {
					whenUploaded: this.DateParsers.parseUTCDate(v.whenUploaded),
					duration: this.DateParsers.parseTimespan(v.duration),
					uploaderFirstName: v.uploaderFirstName || '',
					playlistIndex: i + 1,
					ownerUserId: v.owner?.userId || '',
					ownerUsername: v.owner?.username || '',
					ownerFirstName: v.owner?.firstName || '',
					ownerLastName: v.owner?.lastName || '',
					ownerProfileImageUri: v.owner?.profileImageUri,
					ownerFullName: this.shapeOwnerFullName(v.owner?.firstName, v.owner?.lastName),
					ratingsCount: v.ratingCount
				}));

				if(result.playlistType === PlaylistType.Dynamic) {
					return this.getDynamicPlaylistVideos(accountId, result.searchFilter)
						.then(videos => {
							result.videos = videos;
							return result;
						});
				}

				return result;
			});
	}

	public getUserPlaylists(userId: string): Promise<IPlaylist[]> {
		return lastValueFrom(this.http.get<any>(`/media/users/${userId}/playlists`))
			.then(result => {
				result.playlists.forEach(p => {
					p.whenVideoAddedOrDeleted = p.whenVideoAddedOrDeleted || {};
					p.whenVideoAddedOrDeleted.when = this.DateParsers.parseUTCDate(p.whenVideoAddedOrDeleted.when);
					p.createdBy.when = this.DateParsers.parseUTCDate(p.createdBy.when);
				});

				return result.playlists;
			});
	}

	public modifyFeaturedVideos(videoIds: any[]): Promise<void> {
		return this.PushService.dispatchCommand('media:SaveFeaturedVideos', { videoIds });
	}

	public modifyPublicFeaturedVideos(videoIds: any[]): Promise<void> {
		return this.PushService.dispatchCommand('media:SavePublicFeaturedVideos', { videoIds });
	}

	public modifyPlaylist(playlist: ISavePlaylist): Promise<void> {
		return this.PushService.dispatchCommand('media:SavePlaylistDetails', playlist);
	}

	public removeFeaturedVideo(video: any): Promise<void> {
		return this.PushService.dispatchCommand('media:RemoveFeaturedVideo', video);
	}

	public removePublicFeaturedVideo(video: any): Promise<void> {
		return this.PushService.dispatchCommand('media:RemovePublicFeaturedVideo', video);
	}

	public removeVideoFromPlaylists(videoId: string, userId: string, playlistIds: string[]): Promise<void> {
		return this.PushService.dispatchCommand('media:RemoveVideoFromPlaylists', {
			videoId,
			userId,
			playlistIds
		});
	}

	private getIsActiveParam(isActive: boolean): HttpParams {
		return isActive ? new HttpParams().set('isActive', 'true') : undefined;
	}

	private shapeOwnerFullName(firstName: string, lastName: string): string {
		return `${firstName || ''} ${lastName || ''}`.trim();
	}

	public getDynamicPlaylistVideos(accountId: string, searchFilter: IDynamicPlaylistSeachFilter): Promise<any[]> {
		return this.SearchService.getVideos({
			accountId,
			query: searchFilter.searchTerm,
			parsedQuery: getFiltersQuery(searchFilter),
			sortAscending: !searchFilter.sortDescending,
			sortField: searchFilter.sortField,
			subtitles: true,
			count: 100
		}).then(results => {
			const videos = results.videos;
			videos.forEach(v => v.ownerFullName = v.ownerName);
			return videos;
		});

	}

}
